// "Browse everything" — the Super Admin's All Locations mode.
//
// ── The bug this fixes ─────────────────────────────────────────────────────
// Every room and device read is scoped to a USER, because the backend joins
// through the mapping tables:
//
//   GetRoomsbyUserIDandLocationId       rooms this user is mapped to
//   GetDeviceDetailsbyUserIdAndRoomId   devices this user is mapped to
//   GetCommonDevicesbyUserIDandLocationId
//
// That is right for a customer and wrong for an admin who has just ticked All
// Locations: the location list then covers the whole estate, but the rooms
// query still asks "which of THIS user's rooms are in it" — and for somebody
// else's house the honest answer is none. The screen reads "No rooms" and "No
// devices", which looks like broken data rather than a missing mapping.
//
// So in All Locations mode the reads switch to the endpoints that take no
// UserId at all (verified present on the live server):
//
//   GetRoomsByLocationID   every room in the location
//   GetDevicesByRoomID     every device in the room
//
// ⚠️ THE UNFILTERED ROOM QUERY ALSO SKIPS THE TYPE EXCLUSION. The per-user one
// ends `and a.DeviceId not in (2,13,3,4,19,20,21,26,30,36,45,47,51)`, so a
// curtain or sensor simply is not in the reply and roomMerge exists to put it
// back. `GetDevicesByRoomID` has no such clause — room 1 returns 16 devices
// against the filtered 11, including types 2, 4, 19 and 51. Admin mode
// therefore sees MORE than the owner does, which is the point of it.
//
// ── Why this is a module flag and not a parameter ──────────────────────────
// Twenty-four call sites across eleven files read rooms and devices. Threading
// a flag through all of them means one that gets missed still reads as the
// signed-in user — and the symptom of that is a half-empty screen, which is
// precisely the bug being fixed and gives no clue where it came from. A single
// gate inside the API layer cannot be applied halfway.
//
// The same reasoning already governs core/offline/offlineMode, which every read
// consults the same way.
//
// ⚠️ NOT PERSISTED, and cleared on sign-out. It is a testing lens, not a
// setting: an admin who closes the laptop mid-look must not come back tomorrow
// browsing the whole estate and believing it is their own house.

let browsingAll = false;

const listeners = new Set();

/** Turn the lens on or off. Called from AppContext; nothing else should. */
export function setBrowseAll(on) {
  const next = !!on;
  if (next === browsingAll) return;
  browsingAll = next;
  listeners.forEach((fn) => { try { fn(next); } catch (e) { /* ignore */ } });
}

/** Are reads currently unscoped? */
export const isBrowseAll = () => browsingAll;

/** Subscribe to changes. -> unsubscribe */
export function onBrowseAllChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
